// Canonical engine wire emit for an individual WhatsApp message key.
//
// `BtSlimMessageKey` is the wire/slim shape — every JID-bearing field
// is collapsed to its `_serialized` string by the extension's
// `normalizeWaMessageKey` (chrome-side message-models.ts) BEFORE
// crossing postMessage IPC. Backend's engine→backend boundary consumes
// this slim form; tsc enforces the string contract on both sides.
//
// The raw WAWeb form (with wid objects for `remote`/`participant`) is
// engine-internal — chrome extension projects raw → slim at the IPC
// boundary. Per #74 the raw shape lives inside the chrome extension.
//
// NOTE: a similarly-named TypeScript interface `WaMessageKey` lives in
// `bt-common/utils/message-key.ts`. That's a Baileys-style serializer
// utility (different field set: id/remoteJid/fromMe/participant) used
// by the baileys handlers. Distinct from this schema.

import { z } from 'zod';
import { WireJidSchema, WireKeySchema } from '../../contract/wire-identity';

export const BtSlimMessageKeySchema = z.object({
  // #170: some engine messages (system/service messages) emit a key with no
  // `fromMe`. The field is logically required, but a single such message must
  // not 502 an entire /v1 list_messages batch ("messages.N.waMessageKey.fromMe:
  // Required"). Normalize any non-boolean (missing/null/other) to false so the
  // shape is always uniform, then the boolean contract holds downstream.
  fromMe: z.preprocess((v) => (typeof v === 'boolean' ? v : false), z.boolean()),
  remote: WireJidSchema.optional(),
  id: z.string().optional(),
  _serialized: WireKeySchema.optional(),
  participant: WireJidSchema.optional(),
});

// The runtime schema keeps `_serialized` (it IS a valid wire field once the
// engine has built it). But WhatsApp Web periodically drops `_serialized` from
// its RAW MsgKey model between web builds (e.g. 2.3000.10431xxxxx, 2026-07:
// renamed to a minified field → raw `key._serialized` silently returns
// `undefined`, breaking media download + send-key resolution → duplicate
// sends). To stop new code from reading `_serialized` off a key directly, the
// exported TYPE marks the field `@deprecated` so editors/lint flag every direct
// access — route reads through `getSerialized(key)`
// (extension/wa-communication-layer/core/utils/wa-key-serialization.ts), the
// single accessor that tolerates the model change.
// `_serialized` is REMOVED from the exported TYPE (WhatsApp Web dropped it from
// its raw MsgKey model, 2.3000.10431xxxxx, 2026-07 — a direct read silently
// returns undefined). Every read must route through `getSerialized(key)`
// (extension/wa-communication-layer/core/utils/wa-key-serialization.ts). The
// runtime SCHEMA still accepts `_serialized` (it is a valid wire field once the
// engine has built it) — only the type omits it so tsc flags stale reads.
export type BtSlimMessageKey = Omit<z.infer<typeof BtSlimMessageKeySchema>, '_serialized'>;
